/**
 * Roteador mínimo baseado em history API. Rotas declaradas como padrões
 * ("/processos/:id"); parâmetros de busca lidos com useSearch().
 */
import { useEffect, useSyncExternalStore, type AnchorHTMLAttributes, type ReactNode } from "react";

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());
window.addEventListener("popstate", notify);

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}
const snapshot = () => window.location.pathname + window.location.search;

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (to === snapshot()) return;
  if (opts.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  window.scrollTo(0, 0);
  notify();
}

export function useLocation(): { path: string; search: URLSearchParams } {
  const loc = useSyncExternalStore(subscribe, snapshot);
  const [path, s = ""] = loc.split("?");
  return { path: path || "/", search: new URLSearchParams(s) };
}

export function useSearch(): [URLSearchParams, (patch: Record<string, string | null | undefined>) => void] {
  const { path, search } = useLocation();
  const set = (patch: Record<string, string | null | undefined>) => {
    const next = new URLSearchParams(search);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, v);
    }
    const q = next.toString();
    navigate(`${path}${q ? `?${q}` : ""}`, { replace: true });
  };
  return [search, set];
}

export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split("/").filter(Boolean);
  const a = path.split("/").filter(Boolean);
  if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i]!.startsWith(":")) params[p[i]!.slice(1)] = decodeURIComponent(a[i]!);
    else if (p[i] !== a[i]) return null;
  }
  return params;
}

export function Link({ to, children, ...rest }: { to: string; children: ReactNode } & AnchorHTMLAttributes<HTMLAnchorElement>): JSX.Element {
  return (
    <a
      href={to}
      {...rest}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        rest.onClick?.(e);
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

/** Título da página atual (usado também no caminho "Início › Processos › …"). */
let currentTitle = "";
const titleListeners = new Set<() => void>();
export function usePageTitle(): string {
  return useSyncExternalStore((l) => { titleListeners.add(l); return () => titleListeners.delete(l); }, () => currentTitle);
}

/** Nome do escritório no título da aba ("Processos · Victória Gonçalves"). */
let titleSuffix = "VG APP";
export function setTitleSuffix(name: string | null | undefined): void {
  titleSuffix = name || "VG APP";
  document.title = currentTitle ? `${currentTitle} · ${titleSuffix}` : titleSuffix;
}

export function useTitle(title: string): void {
  useEffect(() => {
    document.title = `${title} · ${titleSuffix}`;
    currentTitle = title;
    titleListeners.forEach((l) => l());
  }, [title]);
}
